import { Box, ColorOptions, Loader, ModBox, Spacer, Text } from '@jfs/ui-new'
import { ButtonNew } from '../../../../components/AdminButton'
import { useEffect, useState } from 'react'
import XlsxPopulate from 'xlsx-populate'
import { EmType } from '../..'
import { ApiConfig } from '../../../../components/ApiConfig'
import Tabbar, { TabbarType } from '../../../../components/Tabbar'
import { useLazyApiData, useStores } from '../../../../data/store'
import { isAccessible } from '../../../../utils'
import { PAYMENT_MODE_SUBTYPES } from '../../../../utils/constants'
import GeneralConfig from '../../../GeneralConfig'
import { PaymentConfig } from '../../../PaymentConfig'
import { ConvenienceFee } from '../ConvenienceFee'
import { PartnerProcessorSettings } from '../PartnerProcessorSettings'
import { RiskSettings } from '../RiskSettings'

interface SettingsProps {
  type: string
  selectedEntity: any
}

const defaultTitles = [{ title: 'General Settings' }, { title: 'Payment Settings' }]
const singleEntityTitles = [
  { title: 'General & API Settings' },
  { title: 'Payment Settings' },
  { title: 'Risk Settings' },
  // { title: 'Convenience Fee' },
  { title: 'Partner/Processor Settings' },
]

const Settings = ({ type, selectedEntity }: SettingsProps) => {
  const {
    getProfileApiAccess,
    isAllPciSelected,
    isAllCommonPciNonPciSelected,
    isAllEmisSelected,
    isAllOthersSelected,
    updateSelectedApisStatus,
    getAllPciList,
    commonPciNonPciList,
    getOthersApisList,
    getEmiList,
    channelId,
    createNewApiMapping,
    profilesaccesslist,
    apisList,
    getAllUpdateableApisList,
    getCreateableProfileChannelIds,
    createNewMultipleApiMapping,
    getUpdateableProfileList,
    updateSelecteProfileStatus,
    getAllCreatableApisList,
    saveApiConfigurations,
    getConfigEntity,
    isEntityPresentInMas,
    searchConfigEntity,
    updateEditMode,
    isEditMode,
    updateSearchConfig,
    updateEmsCards,
    getCardsList,
    createNewConfigEntity,
    removeConfigOfEntity,
    getPaymentsInstrumentsList,
    resetRemovableConfigs,
    resetUpdatableConfig,
    resetNewCreatableConfig,
    // getEmsUpdatableCardsList,
    // createEmsCards,
    resetQrTransactionPattern,
    saveState,
    resetState,
    isConfigHasOwnChannelId,
    getOwnChannelId,
    accessConfig,
    grpAccessConfig,
    resetApiConfigSaveCallDone,
    getConfigDataToDownload,
  } = useStores((store) => ({
    getProfileApiAccess: store.getProfileApiAccess,
    isAllPciSelected: store.onboarding.isAllPciSelected,
    isAllCommonPciNonPciSelected: store.onboarding.isAllCommonPciNonPciSelected,
    isAllEmisSelected: store.onboarding.isAllEmisSelected,
    isAllOthersSelected: store.onboarding.isAllOthersSelected,
    updateSelectedApisStatus: store.updateSelectedApisStatus,
    getAllPciList: store.getAllPciList,
    commonPciNonPciList: store.getCommonInPciNonPci,
    getOthersApisList: store.getOthersApisList,
    getEmiList: store.getEmiList,
    channelId: store.onboarding.channelId,
    createNewApiMapping: store.createNewApiMapping,
    profilesaccesslist: store.onboarding.profilesaccesslist,
    apisList: store.apis,
    getAllUpdateableApisList: store.getAllUpdateableApisList,
    getCreateableProfileChannelIds: store.getCreateableProfileChannelIds,
    createNewMultipleApiMapping: store.createNewMultipleApiMapping,
    getUpdateableProfileList: store.getUpdateableProfileList,
    updateSelecteProfileStatus: store.updateSelecteProfileStatus,
    getAllCreatableApisList: store.getAllCreatableApisList,
    saveApiConfigurations: store.saveApiConfigurations,
    getConfigEntity: store.getConfigEntity,
    isEntityPresentInMas: store.onboarding.isEntityPresentInMas,
    searchConfigEntity: store.searchConfigEntity,
    updateEditMode: store.onboarding.updateEditMode,
    isEditMode: store.onboarding.isEditMode,
    updateSearchConfig: store.updateSearchConfig,
    updateEmsCards: store.updateEmsCards,
    getCardsList: store.onboarding.getEmsCardsList,
    createNewConfigEntity: store.createNewConfigEntity,
    removeConfigOfEntity: store.removeConfigOfEntity,
    getPaymentsInstrumentsList: store.getPaymentsInstrumentsList,
    resetRemovableConfigs: store.resetRemovableConfigs,
    resetUpdatableConfig: store.resetUpdatableConfig,
    resetNewCreatableConfig: store.resetNewCreatableConfig,
    // getEmsUpdatableCardsList: store.onboarding.getEmsUpdatableCardsList,
    // createEmsCards: store.addEmsCards,
    resetQrTransactionPattern: store.onboarding.resetQrTransactionPattern,
    saveState: store.onboarding.saveState,
    resetState: store.onboarding.resetState,
    isConfigHasOwnChannelId: store.onboarding.isConfigHasOwnChannelId(),
    getOwnChannelId: store.onboarding.getOwnChannelId,
    accessConfig: store.user.getAccessConfig(['entity_management_service', 'ems_config']),
    grpAccessConfig: store.user.getAccessConfig(['entity_management_service', 'group']),
    resetApiConfigSaveCallDone: store.resetApiConfigSaveCallDone,
    getConfigDataToDownload: store.getConfigDataToDownload,
  }))

  const [getProfileApiAccessApi, getProfileApiAccessHandler] = useLazyApiData(getProfileApiAccess)

  const [updateApiAccess, updateApiAccessHandler] = useLazyApiData(updateSelectedApisStatus)
  const [createApiMapping, createApiMappingHandler] = useLazyApiData(createNewMultipleApiMapping)
  const [updateSelectedProfileStatusApi, updateSelectedProfileStatusHandler] =
    useLazyApiData(updateSelecteProfileStatus)
  const [createNewApiMappingApi, createNewApiMappingHandler] = useLazyApiData(createNewApiMapping)

  const [updateConfigApi, updateConfigHandler] = useLazyApiData(updateSearchConfig)
  const [updateEmsCardsApi, updateEmsCardsHandler] = useLazyApiData(updateEmsCards)
  // const [createEmsCardsApi, createEmsCardsApiHandler] = useLazyApiData(createEmsCards)
  const [createNewConfigEntityApi, createNewConfigEntityHandler] = useLazyApiData(createNewConfigEntity)
  const [removeNewConfigEntityApi, removeConfigEntityHandler] = useLazyApiData(removeConfigOfEntity)

  const [lazyGetPaymentsInstrumentsApi, paymentsConfigListStateHandler] = useLazyApiData(getPaymentsInstrumentsList)

  const [fetchSearchConfigEntityApi, searchConfigEntityHandler] = useLazyApiData(searchConfigEntity)

  const [isSetShowErrorRetry, setShowErrorAndRetry] = useState(false)

  useEffect(() => {
    if (searchConfigEntityHandler.state === 'rejected' || getPaymentsInstrumentsList.state === 'rejected') {
      setShowErrorAndRetry(true)
    }
    if (searchConfigEntityHandler.state === 'fulfilled') {
      const ownChannelId = getOwnChannelId()
      console.log('ownChannelId: ', ownChannelId)
      if (ownChannelId?.length > 0) {
        getProfileApiAccessApi(ownChannelId)
      }
    }
  }, [searchConfigEntityHandler.state, getPaymentsInstrumentsList.state, getOwnChannelId, getProfileApiAccessApi])

  useEffect(() => {
    if (
      updateEmsCardsHandler.state === 'fulfilled' ||
      updateEmsCardsHandler.state === 'rejected' ||
      updateConfigHandler.state === 'fulfilled' ||
      updateConfigHandler.state === 'rejected'
    ) {
      resetUpdatableConfig()
    }
  }, [resetUpdatableConfig, updateConfigHandler.state, updateEmsCardsHandler.state])

  useEffect(() => {
    if (createNewConfigEntityHandler.state === 'fulfilled' || createNewConfigEntityHandler.state === 'rejected') {
      resetNewCreatableConfig()
    }
  }, [createNewConfigEntityHandler.state, resetNewCreatableConfig])

  useEffect(() => {
    if (removeConfigEntityHandler.state === 'fulfilled' || removeConfigEntityHandler.state === 'rejected') {
      resetRemovableConfigs()
    }
  }, [removeConfigEntityHandler.state, resetRemovableConfigs])

  useEffect(() => {
    if (
      (updateEmsCardsHandler.state === 'fulfilled' || updateEmsCardsHandler.state === 'rejected') &&
      (updateConfigHandler.state === 'fulfilled' || updateConfigHandler.state === 'rejected') &&
      (createNewConfigEntityHandler.state === 'fulfilled' || createNewConfigEntityHandler.state === 'rejected') &&
      (removeConfigEntityHandler.state === 'fulfilled' || removeConfigEntityHandler.state === 'rejected')
    ) {
      resetQrTransactionPattern()
      // fetchSearchConfigEntityApi(selectedEntity.id)
    }
  }, [
    createNewConfigEntityHandler.state,
    fetchSearchConfigEntityApi,
    removeConfigEntityHandler.state,
    resetQrTransactionPattern,
    selectedEntity.id,
    updateConfigHandler.state,
    updateEmsCardsHandler.state,
  ])

  useEffect(() => {
    lazyGetPaymentsInstrumentsApi([
      PAYMENT_MODE_SUBTYPES.NB,
      PAYMENT_MODE_SUBTYPES.WALLET,
      PAYMENT_MODE_SUBTYPES.PAYLATER,
      PAYMENT_MODE_SUBTYPES.CORP_NB,
    ])
  }, [lazyGetPaymentsInstrumentsApi])

  useEffect(() => {
    if (
      (paymentsConfigListStateHandler.state === 'fulfilled' || paymentsConfigListStateHandler.state === 'rejected') &&
      selectedEntity &&
      selectedEntity.id
    ) {
      resetQrTransactionPattern()
      fetchSearchConfigEntityApi(selectedEntity.id)
    }
  }, [fetchSearchConfigEntityApi, paymentsConfigListStateHandler.state, resetQrTransactionPattern, selectedEntity])

  //TODO: EMS: uncomment after demo
  // useEffect(() => {
  //   if (selectedEntity && selectedEntity.id) {
  //     if (isEntityPresentInMas(selectedEntity.id)) {
  //       getConfigEntity(selectedEntity.id)
  //     }
  //   }
  // }, [getConfigEntity, isEntityPresentInMas, selectedEntity])

  const [selectedTabIndex, setSelectedTabIndex] = useState(0)
  // const [isEditMode, setEditMode] = useState(false)
  const [accessData, setAccessData] = useState([])

  const onSelected = (selectedIndex: number) => {
    console.log('selectedIndex:', selectedIndex)
    setSelectedTabIndex(selectedIndex)
  }

  const onEditPress = () => {
    updateEditMode(true)
    saveState()
    resetApiConfigSaveCallDone()
  }

  const onEditCancelPress = () => {
    fetchSearchConfigEntityApi(selectedEntity.id)
    resetState()
    updateEditMode(false)
    resetApiConfigSaveCallDone()
  }

  // useEffect(() => {
  // if (createApiMappingHandler.state === 'fulfilled' || createApiMappingHandler.state === 'rejected') {
  // const updateList = getAllUpdateableApisList()
  // updateApiAccess(updateList, getOwnChannelId())
  // }
  // }, [createApiMappingHandler.state])

  const onSavePostEditPress = () => {
    updateEditMode(false)
    if (type === EmType.createSingleEntity) {
      saveApiConfigurations()
    } else if (type === EmType.Search) {
      createNewConfigEntityApi(selectedEntity?.id)
      removeNewConfigEntityApi(selectedEntity?.id)
      updateConfigApi(selectedEntity?.id)
      if (getCardsList().length > 0) {
        updateEmsCardsApi(selectedEntity?.id, getCardsList())
      }

      // const createList = getCreateableProfileChannelIds(getOwnChannelId())
      // createApiMapping(createList)

      //TODO: uncomment once API Config is enabled in search flow
      // if (isAllPciSelected || isAllCommonPciNonPciSelected || isAllEmisSelected || isAllOthersSelected) {
      //   if (isAllPciSelected) {
      //     updateApiAccess(getAllPciList(), channelId)
      //   }
      //   if (isAllCommonPciNonPciSelected) {
      //     updateApiAccess(commonPciNonPciList(), channelId)
      //   }
      //   if (isAllEmisSelected) {
      //     updateApiAccess(getEmiList(), channelId)
      //   }
      //   if (isAllOthersSelected) {
      //     updateApiAccess(getOthersApisList(), channelId)
      //   }
      // } else {
      //   updateApiAccess(apisList, channelId.length > 0 ? channelId : getOwnChannelId())
      // }

      // updateSelectedProfileStatusApi(apisList, channelId.length > 0 ? channelId : getOwnChannelId())

      saveApiConfigurations()
    }
  }

  const handleRetryOnApiFailure = () => {
    lazyGetPaymentsInstrumentsApi([
      PAYMENT_MODE_SUBTYPES.NB,
      PAYMENT_MODE_SUBTYPES.WALLET,
      PAYMENT_MODE_SUBTYPES.PAYLATER,
      PAYMENT_MODE_SUBTYPES.CORP_NB,
    ])
    fetchSearchConfigEntityApi(selectedEntity.id)
    setShowErrorAndRetry(false)
  }

  useEffect(() => {
    if (getProfileApiAccessHandler.state === 'fulfilled') {
      if (profilesaccesslist && profilesaccesslist.length > 0) {
        console.log('===> profilesaccesslist', JSON.stringify(profilesaccesslist))
        setAccessData(profilesaccesslist)
      }
    }
  }, [getProfileApiAccessHandler.state, profilesaccesslist])

  const onDownloadPress = () => {
    console.log('onDownloadPress')
    const inputKeys = ['mid', 'config', 'configList']

    const modifiedMidData = JSON.parse(JSON.stringify(getConfigDataToDownload()))
    console.log('onDownloadPress', modifiedMidData)

    XlsxPopulate.fromBlankAsync().then(async (workbook) => {
      const data: any = []
      data.push([...inputKeys])
      data.push([
        modifiedMidData?.mid,
        (modifiedMidData?.config && JSON.stringify(modifiedMidData?.config)) ?? '',
        (modifiedMidData?.configList && JSON.stringify(modifiedMidData?.configList)) ?? '',
      ])

      workbook.sheet(0)?.cell('A1').value(data)

      const blob = await workbook.outputAsync({
        type: 'blob',
      })
      var url = window.URL.createObjectURL(blob as Blob)
      var a = document.createElement('a')
      document.body.appendChild(a)
      a.href = url
      a.download = 'bulk_config_file.xlsx'
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    })
  }

  return (
    <>
      <ModBox show={isSetShowErrorRetry} height="100%">
        <Box alignSelf="center" justifyContent="center" height="100%" alignItems="center">
          <Text fontSize={16} color="BLACK" fontWeight={'BOLD'}>
            {'An error has occured while fetching settings'}
          </Text>
          <Spacer direction="vertical" size={20} />
          <ButtonNew onPress={handleRetryOnApiFailure} title="Retry" width={100} height={36} />
        </Box>
      </ModBox>
      <ModBox
        f={1}
        overflow={'auto'}
        ph={40}
        pv={40}
        show={!isSetShowErrorRetry}
        backgroundColor={'WHITE'}
        nativeID="vertical-scroll"
        h={'100%'}>
        <Box direction="row" justify="space-between">
          <Text title={'Settings'} color={'BLACK_21'} fontSize={18} fontFamily={'jiotype-bold'} />
          <ModBox show={false} backgroundColor={'#FBF6EC' as ColorOptions} borderRadius={2} padding={4} marginLeft={16}>
            <Text
              title={'Below information is fetched from MAS.'}
              color={'#A97425' as ColorOptions}
              fontSize={12}
              fontFamily={'jiotype-medium'}
              fontStyle={'italic'}
            />
          </ModBox>
          <ModBox show={true}>
            <ModBox show={isEditMode && (selectedTabIndex < 2 || selectedTabIndex === 3)} direction={'row'}>
              <ButtonNew
                type={'secondarySmall'}
                borderWidth={1}
                title={'Cancel'}
                borderColor={'#0070B6'}
                borderRadius={4}
                onPress={onEditCancelPress}
              />
              <Spacer size={16} direction={'horizontal'} />
              <ButtonNew
                height={32}
                borderWidth={1}
                title={'Save Changes'}
                borderColor={'#0070B6'}
                borderRadius={4}
                onPress={onSavePostEditPress}
                color={'#0070B6'}
              />
            </ModBox>
            <ModBox
              direction="row"
              show={
                !isEditMode && (selectedTabIndex < 2 || selectedTabIndex === 3) && isAccessible(accessConfig, 'create')
              }>
              <ButtonNew
                type={'secondarySmall'}
                borderWidth={1}
                title={'Download'}
                borderColor={'#0070B6'}
                borderRadius={4}
                icon="DOWNLOAD"
                onPress={onDownloadPress}
              />
              <Spacer size={16} direction={'horizontal'} />
              <ButtonNew
                type={'secondaryIconSmall'}
                borderWidth={1}
                title={'Edit'}
                borderColor={'#0070B6'}
                borderRadius={4}
                icon="EDIT"
                onPress={onEditPress}
                hide={!isAccessible(grpAccessConfig, 'create')}
              />
            </ModBox>
          </ModBox>
        </Box>
        <ModBox
          show={
            updateEmsCardsHandler.state === 'pending' ||
            createNewConfigEntityHandler.state === 'pending' ||
            removeConfigEntityHandler.state === 'pending' ||
            paymentsConfigListStateHandler.state === 'pending' ||
            searchConfigEntityHandler.state === 'pending'
          }>
          <Loader color="PRIMARY" />
        </ModBox>
        <ModBox
          marginTop={16}
          marginBottom={32}
          show={
            !(
              updateEmsCardsHandler.state === 'pending' ||
              createNewConfigEntityHandler.state === 'pending' ||
              removeConfigEntityHandler.state === 'pending' ||
              paymentsConfigListStateHandler.state === 'pending' ||
              searchConfigEntityHandler.state === 'pending'
            )
          }>
          <Tabbar
            titles={type === EmType.createSingleEntity || type === EmType.Search ? singleEntityTitles : defaultTitles}
            default={selectedTabIndex}
            type={TabbarType.recentPayments}
            onSelected={onSelected}
          />
          <ModBox show={selectedTabIndex === 0} minHeight={508}>
            <Box f={1} overflow={'auto'} pv={32}>
              <Box minHeight={300}>
                <GeneralConfig isOnboarding={true} isEdit={isEditMode} selectedEntity={selectedEntity} type={type} />
              </Box>
              {/*  */}
              <ModBox minHeight={500} show={isConfigHasOwnChannelId}>
                <ApiConfig
                  type="EM"
                  channelId={'8026'}
                  accessData={accessData}
                  parentEditMode={isEditMode}
                  isOnboarding={false}
                />
              </ModBox>
            </Box>
          </ModBox>
          <ModBox show={selectedTabIndex === 1} minHeight={508}>
            <PaymentConfig type={'EM'} isEdit={isEditMode} mid={selectedEntity?.id} />
          </ModBox>
          <ModBox show={selectedTabIndex === 2} minHeight={508}>
            <RiskSettings
              isEdit={isEditMode}
              isSearchFlow={type === EmType.Search || type === EmType.createSingleEntity}
              selectedEntity={selectedEntity}
            />
          </ModBox>
          <ModBox show={selectedTabIndex === 3} minHeight={508}>
            <PartnerProcessorSettings isEdit={isEditMode} />
          </ModBox>
          <ModBox show={selectedTabIndex === 4} minHeight={508}>
            <ConvenienceFee />
          </ModBox>
        </ModBox>
      </ModBox>
    </>
  )
}

export { Settings }
